/**
 * Welke toestand een antwoord zónder cijfers aanwijst — en dat zijn er twee, geen één.
 *
 * AD-25 laat elke run een spoor na: geen weekdocument betekent "nog niet gedraaid", een document met
 * `status: 'mislukt'` betekent mislukt, en alleen `status: 'geslaagd'` draagt getallen. Het antwoord
 * geeft dat verschil door als `reden` — `nog_niet_gemeten` tegenover `meting_mislukt`
 * (`dqs-service/src/plugins/werkkwaliteit/index.ts`, `zonderMeting`).
 *
 * De weergave maakte er tot en met story 4.5 één toestand van: élk antwoord met `data: null` kwam
 * als "nog geen rapport voor deze week" op het scherm. Dat is precies wat AD-27 verbiedt — een
 * storing die als een lege week leest, zegt over iemands werk iets wat niemand heeft gemeten.
 */

export type Weektoestand = 'nog_niet_gemeten' | 'meting_mislukt';

/**
 * De twee oorzaken die de storingsmelding kent, als constante en niet als losse tekst in de JSX.
 *
 * De lintregel `i18next/no-literal-string` leest élke kleine letter in een JSX-attribuut als tekst
 * voor een gebruiker, en terecht: dat is precies hoe een Nederlandse zin ongemerkt in de code komt.
 * Een benoemde constante zegt bovendien wat de waarde is in plaats van hem te herhalen.
 */
export const STORING_ONBEREIKBAAR = 'onbereikbaar' as const;
export const STORING_MISLUKT = 'meting_mislukt' as const;

export type Storingsoorzaak = typeof STORING_ONBEREIKBAAR | typeof STORING_MISLUKT;

/**
 * De toestand die bij een reden hoort.
 *
 * **Alles wat niet letterlijk `nog_niet_gemeten` is, is een storing.** Dat is de veilige kant en niet
 * de nette: "er is deze week niets gemeten" is een uitspraak over iemands werk, en die mag alleen
 * gedaan worden op een reden die dat met zoveel woorden zegt. Komt er een reden bij die dit portaal
 * niet kent, dan leest zij als storing tot iemand haar hier toevoegt.
 */
export function weektoestand(reden: string | undefined): Weektoestand {
  return reden === 'nog_niet_gemeten' ? 'nog_niet_gemeten' : 'meting_mislukt';
}

/**
 * De week die in de kiezer onder `week` staat, of `null`.
 *
 * Waar de storing naartoe verwijst. Het antwoord noemt géén weeksleutel van de laatste geslaagde
 * meting — `laatsteGeslaagdeRun` is het moment waarop een run draaide en niet de week die hij mat
 * (`opslag.ts`, `berekendOp`) — dus de link kan niet beweren dat de vorige week wél lukte. Wat hij
 * wél kan: de lezer één week terugbrengen, waar dezelfde melding met dezelfde link staat zolang ook
 * die week mislukte. Zo eindigt de keten bij de eerste week die het wel deed, zonder dat het scherm
 * ergens iets beweert dat niemand heeft gemeten.
 */
export function vorigeWeekVan(weken: readonly string[], week: string): string | null {
  const plek = weken.indexOf(week);
  if (plek === -1) return null;
  const vorige = weken[plek + 1] ?? null;
  // Staat dezelfde week twee keer in de lijst, dan wijst `indexOf` naar de eerste en is de volgende
  // diezelfde week — een link naar het scherm waar de lezer al staat. Die brengt hem nergens, dus
  // dan is er geen weg terug (aangescherpt 4 september 2026).
  return vorige === week ? null : vorige;
}
